import { INITIAL_HITS_PER_PAGE } from "@components/commerce/product/list/product-list-footer/ProductListFooter";
import type { IPartFilter, IPartSortByOption } from "@contentful-api/types/contentful";
import { useStore } from "@store/index";
import { useDisclosure } from "@ui/hooks/useDisclosure";
import type { FC, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { type ConfigureProps, useClearRefinements, useConfigure } from "react-instantsearch";
import { FiltersContext } from "./FiltersContext";
import { DEFAULT_ATTRIBUTES_TO_RETRIEVE } from "@lib/algolia/constants";
import { AlgoliaProductProperties } from "@lib/wrappers/algolia";

interface IFiltersControllerProps {
    children: ReactNode;
    listOfFilters: IPartFilter[];
    sortByOptionsList: IPartSortByOption[];
    isMenPage?: boolean;
    pageCategory?: string;
    page?: number;
}

export const FiltersController: FC<IFiltersControllerProps> = ({
    children,
    listOfFilters,
    sortByOptionsList,
    isMenPage = true,
    pageCategory,
    page = 1,
}) => {
    useConfigure({
        filters: pageCategory ? `categories:"${pageCategory}" AND hasStock:true` : "hasStock:true",
        hitsPerPage: INITIAL_HITS_PER_PAGE * page,
        facetingAfterDistinct: true,
        attributesToRetrieve: [
            ...DEFAULT_ATTRIBUTES_TO_RETRIEVE,
            AlgoliaProductProperties.descriptiveHeading,
        ],
        page: 0,
    } as ConfigureProps);

    const globalShoeSizeConvention = useStore((state) => state.data.me?.globalShoeSizeConvention);
    const setGlobalShoeSizeConvention = useStore((state) => state.setGlobalShoeSizeConvention);

    const filtersMap = {};
    listOfFilters?.forEach((filter) => {
        filtersMap[filter?.fields?.filterName] = { refine: null, items: [] };
    });

    const [filterItems, setFilterItems] = useState(filtersMap);

    const [sizeModalOpen, setSizeModalOpen] = useState(false);

    const [mobileSortByOpen, setMobileSortByOpen] = useState(false);

    const [sortByOptions, setSortByOptions] = useState({
        options: [],
        refine: null,
        currentRefinement: null,
    });

    const { refine: clearRefinements } = useClearRefinements();

    const {
        onOpen: openFiltersDialog,
        onClose: closeFiltersDialog,
        isOpen: filtersDialogOpened,
        onToggle: setFiltersDialogOpened,
    } = useDisclosure();

    const applySelectedRefinementsFromDialog = useCallback(
        (items) => {
            clearRefinements();
            items.forEach((filter) =>
                filter.refinements.forEach((refinement) => {
                    const filter = listOfFilters.find(
                        (it) => it.fields?.facetName === refinement.attribute
                    );
                    filterItems[filter.fields?.filterName].refine(refinement.value);
                })
            );
            closeFiltersDialog();
        },
        [filterItems]
    );

    const contextValue = useMemo(
        () => ({
            facets: filterItems,
            sortByOptions,
            setSortByOptions,
            setFilterItems,
            setFiltersDialogOpened,
            filtersDialogOpened,
            openFiltersDialog,
            closeFiltersDialog,
            sizeLocale: globalShoeSizeConvention,
            setSizeLocale: setGlobalShoeSizeConvention,
            isMenPage,
            sortByOptionsList,
            sizeModalOpen,
            setSizeModalOpen,
            applySelectedRefinementsFromDialog,
            clearRefinements,
            mobileSortByOpen,
            setMobileSortByOpen,
        }),
        [
            filterItems,
            sortByOptions,
            setSortByOptions,
            setFilterItems,
            setFiltersDialogOpened,
            filtersDialogOpened,
            openFiltersDialog,
            closeFiltersDialog,
            mobileSortByOpen,
            setMobileSortByOpen,
            isMenPage,
            sortByOptionsList,
            sizeModalOpen,
            setSizeModalOpen,
            applySelectedRefinementsFromDialog,
            clearRefinements,
        ]
    );

    return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};
